<template>
  <article class="CardCredits" :class="{ 'CardCredits--featured': featured }">
    <div v-if="tag || featured" class="CardCredits-tags">
      <Tag
        v-if="tag"
        type="neutral"
        variant="subtle"
        size="xs"
      >
        {{ tag }}
      </Tag>

      <Tag
        v-if="featured"
        type="brand-primary"
        variant="filled"
        size="xs"
        leftIcon="trending-up"
        class="CardCredits-featuredTag"
      >
        {{ t('ui.components.cardCredits.featured') }}
      </Tag>
    </div>

    <h3 class="CardCredits-title">
      {{ title }}
    </h3>

    <p class="CardCredits-credits">
      <span class="CardCredits-creditsValue">{{ credits }}</span>
      <span class="CardCredits-creditsLabel">{{ creditsLabel }}</span>
    </p>

    <PlanBenefits v-if="benefits?.length" class="CardCredits-benefits" :benefits />

    <div class="CardCredits-price">
      <div class="CardCredits-priceGroup">
        <span v-if="priceLabel" class="CardCredits-priceLabel">{{ priceLabel }}</span>
        <span class="CardCredits-priceValue">{{ price }}</span>
        <span v-if="frequency" class="CardCredits-frequency">/{{ frequency }}</span>
        <Tooltip v-if="priceInfo">
          <template #trigger>
            <Icon icon="information" size="xs" class="CardCredits-infoIcon" />
          </template>
          {{ priceInfo }}
        </Tooltip>
      </div>

      <span v-if="pricePerCredit" class="CardCredits-pricePerCredit">{{ pricePerCredit }}</span>
    </div>

    <ActionButton
      v-if="submitLabel"
      :size="isMobile ? 'md' : 'lg'"
      rightIcon="arrow-right"
      flipRtl
      class="CardCredits-submit"
      @click="emit('submit')"
    >
      {{ submitLabel }}
    </ActionButton>
  </article>
</template>

<script setup lang="ts">
import { useI18n } from '@cvo/i18n/composables/useI18n';
import { useResponsive } from '@cvo/ui/composables';
import ActionButton from '../../atoms/ActionButton/ActionButton.vue';
import Icon from '../../atoms/Icon/Icon.vue';
import Tag from '../../atoms/Tag/Tag.vue';
import Tooltip from '../../atoms/Tooltip/Tooltip.vue';
import PlanBenefits from '../../molecules/PlanBenefits/PlanBenefits.vue';
import type { CardCreditsEmits, CardCreditsProps } from './CardCredits.model';

defineProps<CardCreditsProps>();
const emit = defineEmits<CardCreditsEmits>();

const { t } = useI18n();
const { isMobile } = useResponsive();
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/responsive';
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .CardCredits {
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-6;
    padding: vars.$dimensions-d-6;

    background-color: vars.$organisms-card-credits-bg-color;
    border: vars.$organisms-card-credits-border-stroke solid vars.$organisms-card-credits-border-color;
    border-radius: vars.$organisms-card-credits-lg-md-border-radius;
    overflow: hidden;

    @include responsive.mobile {
      gap: vars.$dimensions-d-4;
      padding: vars.$dimensions-d-4;
      border-radius: vars.$organisms-card-credits-sm-border-radius;
    }

    &-tags {
      display: flex;
      flex-wrap: wrap;
      gap: vars.$dimensions-d-2;
    }

    &-title {
      margin: 0;
      color: vars.$organisms-card-credits-primary-text-color;
      @include text.heading-responsive('h4', 'h3');
    }

    &-credits {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin: 0;
      padding: vars.$dimensions-d-4;

      color: vars.$organisms-card-credits-creditsvalue-text-color;
      text-align: center;
      background-color: vars.$organisms-card-credits-creditsvalue-bg-color;
      border: vars.$organisms-card-credits-creditsvalue-border-stroke solid vars.$organisms-card-credits-creditsvalue-border-color;
      border-radius: vars.$organisms-card-credits-creditsvalue-border-radius;

      @include responsive.mobile {
        padding: vars.$dimensions-d-3;
      }
    }

    &--featured &-credits {
      background-color: vars.$organisms-card-credits-creditsvalue-featured-bg-color;
      border-color: vars.$organisms-card-credits-creditsvalue-featured-border-color;
    }

    &-creditsValue {
      @include responsive.mobile {
        @include text.heading('h1');
      }

      @include responsive.tablet {
        @include text.heading-base(vars.$font-size-display, normal);
      }
    }

    &-creditsLabel {
      @include responsive.mobile {
        @include text.text('md', vars.$typography-weight-strong);
      }

      @include responsive.tablet {
        @include text.text('lg', vars.$typography-weight-strong);
      }
    }

    &-benefits {
      @include responsive.mobile {
        .PlanBenefits-list {
          gap: vars.$dimensions-d-2;
        }

        .PlanBenefits-icon {
          --icon-size: #{vars.$dimensions-d-4};
        }

        .PlanBenefits-text {
          @include text.text('sm');
        }
      }
    }

    &-price {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-2;
      // Anchors price and button to the card bottom so cards in a row stay aligned.
      margin-block-start: auto;

      color: vars.$organisms-card-credits-secondary-text-color;
      @include text.text('sm');

      @include responsive.mobile {
        gap: 0;
      }
    }

    &-priceGroup {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: vars.$dimensions-d-2;

      @include responsive.mobile {
        gap: vars.$dimensions-d-1;
      }
    }

    &-priceLabel {
      @include responsive.mobile {
        margin-inline-end: vars.$dimensions-d-2;
      }
    }

    &-priceValue {
      color: vars.$organisms-card-credits-primary-text-color;
      @include text.heading-responsive('h3', 'h1');
    }

    &-frequency {
      @include text.text('lg');
    }

    &-infoIcon {
      color: vars.$organisms-card-credits-disclaimer-text-color;

      @include responsive.mobile {
        --icon-size: #{vars.$dimensions-d-4};
      }
    }
  }
}
</style>
